import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  Timestamp,
  where,
  getDoc
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { Contract } from "types/contract";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "./usePermissions";
import { useNotifications } from "./useNotifications";
import { useLanguage } from "../contexts/LanguageContext";

export const useContracts = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { canAdmin } = usePermissions();
  const { createNotification } = useNotifications();
  const { t } = useLanguage();

  const loadContracts = useCallback(async () => {
    try {
      setLoading(true);
      const contractsRef = collection(db, 'contracts');
      let q = query(contractsRef, orderBy('createdAt', 'desc'));

      // A super_admin és admin minden szerződést láthat
      // Egyébként szűrünk organizationId alapján, ha van beállítva
      if (user?.role === "super_admin") {
        q = query(contractsRef, orderBy('createdAt', 'desc'));
      } else if (!canAdmin('contracts') && user?.organizationId) {
        q = query(contractsRef, where('organizationId', '==', user.organizationId), orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);

      const loadedContracts = snapshot.docs.map((doc) => {
        const data = doc.data();

        // Kezeljük a shareSettings-t is, ha létezik
        let shareSettings = undefined;
        if (data.shareSettings) {
          shareSettings = {
            ...data.shareSettings,
            // Konvertáljuk a Timestamp típusú mezőket Date objektumokká
            createdAt: data.shareSettings.createdAt?.toDate ? data.shareSettings.createdAt.toDate() :
              data.shareSettings.createdAt || new Date(),
            validUntil: data.shareSettings.validUntil?.toDate ? data.shareSettings.validUntil.toDate() :
              data.shareSettings.validUntil
          };
        }

        return {
          id: doc.id,
          name: data.name,
          type: data.type,
          templateId: data.templateId,
          templateName: data.templateName,
          fileUrl: data.fileUrl,
          fileName: data.fileName,
          status: data.status || 'draft',
          validStart: data.validStart?.toDate(),
          validEnd: data.validEnd?.toDate(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          shareSettings: shareSettings,
          customerId: data.customerId,
          customerName: data.customerName,
          organizationId: data.organizationId
        } as Contract;
      });

      setContracts(loadedContracts);
      setError(null);
    } catch (err) {
      console.error("Hiba a szerződések betöltése során:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [user, canAdmin]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const addContract = useCallback(async (contractData: Omit<Contract, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const contractsRef = collection(db, 'contracts');

      // Filter out undefined values to prevent Firestore errors
      const filteredContractData = Object.entries(contractData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      const newContract = {
        ...filteredContractData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(contractsRef, newContract);

      // Frissítsük a helyi állapotot is
      setContracts(prevContracts => [
        {
          ...contractData,
          id: docRef.id,
          createdAt: new Date(),
          updatedAt: new Date()
        } as Contract,
        ...prevContracts
      ]);

      // Értesítés létrehozása
      try {
        if (user?.organizationId) {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const title = translations.subjects?.contractCreated 
            ? translations.subjects.contractCreated.replace('{{contractName}}', contractData.name)
            : `${lang === 'hu' ? 'Új szerződés létrehozva' : 'New contract created'}: ${contractData.name}`;
            
          await createNotification({
            type: 'system',
            title: title,
            message: `${contractData.name} ${lang === 'hu' ? 'szerződés sikeresen létrehozva' : 'contract successfully created'}.`,
            priority: 'medium',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/contracts',
            data: { contractId: docRef.id }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }

      return docRef.id;
    } catch (err) {
      console.error("Hiba a szerződés hozzáadása során:", err);
      throw err;
    }
  }, []);

  const updateContract = useCallback(async (id: string, contractData: Partial<Omit<Contract, 'id' | 'createdAt' | 'updatedAt'>>) => {
    try {
      const contractRef = doc(db, 'contracts', id);

      const updateData = {
        ...contractData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(contractRef, updateData);

      // Frissítsük a helyi állapotot is azonnal
      setContracts(prevContracts =>
        prevContracts.map(contract =>
          contract.id === id
            ? {
              ...contract,
              ...contractData,
              updatedAt: new Date()
            }
            : contract
        )
      );

      // Értesítés létrehozása ha státusz változott
      try {
        if (contractData.status && user?.organizationId) {
          const lang = t('common.language') === 'English' ? 'en' : 'hu';
          const translations = lang === 'en' 
            ? (await import('../translations/en')).default.notifications
            : (await import('../translations/hu')).default.notifications;
            
          const statusLabel = (translations.statuses as any)?.[contractData.status] || contractData.status;
          const priority = contractData.status === 'valid' ? 'medium' : contractData.status === 'expired' ? 'high' : 'low';
          
          const contractName = contractData.name || (lang === 'hu' ? 'Egy szerződés' : 'A contract');
          const title = translations.subjects?.contractStatusChanged 
            ? translations.subjects.contractStatusChanged.replace('{{contractName}}', contractName)
            : `${lang === 'hu' ? 'Szerződés státusz változott' : 'Contract status changed'}: ${contractName}`;
          
          await createNotification({
            type: 'system',
            title: title,
            message: `${contractName} ${lang === 'hu' ? 'státusza' : 'status'}: ${statusLabel}`,
            priority: priority as 'low' | 'medium' | 'high',
            userId: user.id,
            organizationId: user.organizationId,
            createdBy: user.id,
            createdByName: user.displayName,
            actionUrl: '/contracts',
            data: { contractId: id }
          });
        }
      } catch (notifError) {
        console.error('Error creating notification:', notifError);
      }
    } catch (err) {
      console.error("Hiba a szerződés frissítése során:", err);
      throw err;
    }
  }, []);

  const deleteContract = useCallback(async (id: string) => {
    try {
      const contractRef = doc(db, 'contracts', id);
      const contractDoc = await getDoc(contractRef);

      if (!contractDoc.exists()) {
        throw new Error("A szerződés nem található");
      }

      const contractData = contractDoc.data();

      // Töröljük a dokumentumot a megfelelő helyről a típus alapján
      if (contractData.type === 'contract' && contractData.customerId) {
        const customerRef = doc(db, 'customers', contractData.customerId);
        const customerDoc = await getDoc(customerRef);

        if (customerDoc.exists()) {
          const customerData = customerDoc.data();
          if (customerData.documents) {
            // Kiszűrjük a törlendő dokumentumot a fájl URL alapján
            const updatedDocuments = customerData.documents.filter((doc: any) => doc.url !== contractData.fileUrl);
            await updateDoc(customerRef, {
              documents: updatedDocuments,
              updatedAt: serverTimestamp()
            });
          }
        }
      } else if (contractData.type === 'quotation') {
        const quotationRef = doc(db, 'quotations', contractData.quotationId);
        const quotationDoc = await getDoc(quotationRef);

        if (quotationDoc.exists()) {
          const quotationData = quotationDoc.data();
          if (quotationData.documents) {
            // Kiszűrjük a törlendő dokumentumot a fájl URL alapján
            const updatedDocuments = quotationData.documents.filter((doc: any) => doc.url !== contractData.fileUrl);
            await updateDoc(quotationRef, {
              documents: updatedDocuments,
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Töröljük a szerződést
      await deleteDoc(contractRef);

      // Frissítsük a helyi állapotot is
      setContracts(prevContracts =>
        prevContracts.filter(contract => contract.id !== id)
      );
    } catch (err) {
      console.error("Hiba a szerződés törlése során:", err);
      throw err;
    }
  }, []);

  return {
    contracts,
    loading,
    error,
    refreshContracts: loadContracts,
    addContract,
    updateContract,
    deleteContract
  };
};
